import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  Typography, Box, Button, CircularProgress, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Outlet, Link, useLocation } from "react-router-dom";

export default function MyStats() {
  const auth = useAuth();
  const token = auth.user?.access_token || auth.user?.id_token;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const location = useLocation();
  const [showAllStats, setShowAllStats] = useState(false);
  const [allStats, setAllStats] = useState(null);
  const [allStatsLoading, setAllStatsLoading] = useState(false);
  const [allStatsError, setAllStatsError] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await fetch("https://elk87mfiog.execute-api.us-east-1.amazonaws.com/my-golf-stats", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        const data = await res.json();
        if (res.ok) {
          setStats(data);
        } else {
          setError(data.message || "Failed to load stats.");
        }
      } catch (err) {
        setError("Error: " + err.message);
      }
      setLoading(false);
    };
    if (token) fetchStats();
  }, [token]);

  useEffect(() => {
    const fetchAllStats = async () => {
      setAllStatsLoading(true);
      setAllStatsError("");
      try {
        const res = await fetch("https://elk87mfiog.execute-api.us-east-1.amazonaws.com/my-golf-stats-data", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        const data = await res.json();
        if (res.ok) {
          setAllStats(data.all_stats || []);
        } else {
          setAllStatsError(data.message || "Failed to load all stats.");
        }
      } catch (err) {
        setAllStatsError("Error: " + err.message);
      }
      setAllStatsLoading(false);
    };
    if (showAllStats && token && allStats === null) {
      fetchAllStats();
    }
  }, [showAllStats, token, allStats]);

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this stat entry?")) return;
    try {
      const res = await fetch(
        "https://elk87mfiog.execute-api.us-east-1.amazonaws.com/my-golf-stats-data",
        {
          method: "DELETE",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            user_id: row.user_id,
            timestamp: row.timestamp
          })
        }
      );
      if (res.ok) {
        setAllStats((prev) =>
          prev.filter(
            (r, idx) =>
              !(
                r.date === row.date &&
                r.course === row.course &&
                idx === allStats.indexOf(row)
              )
          )
        );
      } else {
        alert("Failed to delete stat entry.");
      }
    } catch (err) {
      alert("Error deleting stat entry: " + err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return <Typography>No stats found.</Typography>;
  }

  return (
    <>
      {location.pathname === "/my-stats/input" ? (
        <Outlet />
      ) : (
        <>
          <img
            src="/logo192.png"
            alt="Logo192"
            width={100}
            height={100}
            style={{ display: "block", margin: "0 auto" }}
          />
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button component={Link} to="input" variant="contained" color="primary">
              Input Stats
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
              onClick={() => setShowAllStats((prev) => !prev)}
            >
              {showAllStats ? "Hide My Stats Data" : "Show My Stats Data"}
            </Button>
          </Box>
          {location.pathname !== "/my-stats/input" && !showAllStats && (
            <Paper sx={{ mt: 4, maxWidth: 600, mx: "auto", p: 3, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                {`My ${new Date().getFullYear()} Stats`}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Total Birdies</TableCell>
                      <TableCell align="center">Total Eagles</TableCell>
                      <TableCell align="center">18-hole Scoring Average</TableCell>
                      <TableCell align="center">Rounds Played</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center">{stats.total_birdies}</TableCell>
                      <TableCell align="center">{stats.total_eagles}</TableCell>
                      <TableCell align="center">{stats.avg_score}</TableCell>
                      <TableCell align="center">{stats.total_rounds_played}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">Birdie Rate</TableCell>
                      <TableCell align="center">Average Putts</TableCell>
                      <TableCell align="center">GIR</TableCell>
                      <TableCell align="center">Driving Accuracy</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">{stats.birdie_rate}</TableCell>
                      <TableCell align="center">{stats.avg_putts}</TableCell>
                      <TableCell align="center">{`${stats.gir}%`}</TableCell>
                      <TableCell align="center">{`${stats.driving_accuracy}%`}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">Walking Percentage</TableCell>
                      <TableCell align="center">Lost Ball Rate</TableCell>
                      <TableCell align="center">Courses Played</TableCell>
                      <TableCell align="center">Money Earnings</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">{`${stats.walking_rate}%`}</TableCell>
                      <TableCell align="center">{stats.lost_ball_rate}</TableCell>
                      <TableCell align="center">{stats.courses_played}</TableCell>
                      <TableCell align="center">{`$${stats.money_earned}`}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">9-hole Scoring Average</TableCell>
                      <TableCell align="center">Rounds in 70s</TableCell>
                      <TableCell align="center">Rounds in 80s</TableCell>
                      <TableCell align="center">Rounds in 90s</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center">{stats.average_score_9}</TableCell>
                      <TableCell align="center">{stats.rounds_in_70s}</TableCell>
                      <TableCell align="center">{stats.rounds_in_80s}</TableCell>
                      <TableCell align="center">{stats.rounds_in_90s}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {showAllStats && (
            <TableContainer component={Paper} sx={{ mt: 4, maxWidth: 900, mx: "auto", borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" align="center" sx={{ mt: 2, mb: 2 }}>
                All Stat Entries
              </Typography>
              {allStatsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : allStatsError ? (
                <Alert severity="error">{allStatsError}</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Course</TableCell>
                      <TableCell>Nine Hole Round</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Putts</TableCell>
                      <TableCell>GIR</TableCell>
                      <TableCell>Driving Accuracy</TableCell>
                      <TableCell>Walked</TableCell>
                      <TableCell>Lost Balls</TableCell>
                      <TableCell>Course Length</TableCell>
                      <TableCell>Slope</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Money Earned</TableCell>
                      <TableCell>Handicap</TableCell>
                      <TableCell>Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allStats && allStats.map((row, idx) => (
                      <TableRow key={row.date + row.course + idx}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.course}</TableCell>
                        <TableCell>{row.nine_hole ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.score}</TableCell>
                        <TableCell>{row.putts}</TableCell>
                        <TableCell>{row.gir}</TableCell>
                        <TableCell>{row.driving_accuracy}</TableCell>
                        <TableCell>{row.walked ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.lost_balls}</TableCell>
                        <TableCell>{row.course_length}</TableCell>
                        <TableCell>{row.slope}</TableCell>
                        <TableCell>{row.rating}</TableCell>
                        <TableCell>{`$${row.money}`}</TableCell>
                        <TableCell>{row.handicap}</TableCell>
                        <TableCell>
                          <Button
                            color="error"
                            size="small"
                            onClick={() => handleDelete(row)}
                            startIcon={<DeleteIcon />}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          )}
          <Outlet />
        </>
      )}
    </>
  );
}